import { FileLock2 } from '@orgus/ui/icons';
import { useState } from 'react';
import { ErrorState, RelativeTime, Segmented, Skeleton, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useDataSharing } from '../../policy/useDataSharing.ts';
import type { SettingsSection } from '../registry.ts';

type Mode = 'content' | 'metadata';

const ALWAYS = ['Machine facts: name, system, load, agent version', 'Session status, folder, model, tokens and cost', 'Commands people run from Orgus, and approvals'];
const CONTENT = ['Prompts, replies and subagent tasks', 'Session timelines and transcript search', 'Diffs of the changes sessions make'];

function DataSentSettings() {
  const { state, retry, save } = useDataSharing();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);

  if (state.kind === 'loading') {
    return (
      <div className="flex flex-col gap-2" role="status" aria-label="Loading the data setting">
        <Skeleton className="h-control w-80" />
        <Skeleton className="h-16" />
      </div>
    );
  }
  if (state.kind === 'error') return <ErrorState error={state.error} onRetry={retry} />;

  const mode: Mode = state.data.content ? 'content' : 'metadata';
  const change = async (m: Mode) => {
    setBusy(true);
    setError(null);
    try {
      await save(m === 'content');
    } catch (e) {
      setError(toUiError(e, { what: 'The data setting was not saved.' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h3 className="text-ui font-medium text-fg">Send transcript and diff content</h3>
        <Segmented<Mode>
          label="Send transcript and diff content"
          size="md"
          options={[
            { value: 'content', label: 'Transcripts and diffs', disabled: busy },
            { value: 'metadata', label: 'Metadata only', disabled: busy },
          ]}
          value={mode}
          onChange={(m) => void change(m)}
        />
        {error && <ErrorState error={error} variant="inline" />}
        {state.data.updatedAt && (
          <p className="text-caption text-fg-subtle">
            Changed by {state.data.updatedBy ?? 'someone'} <RelativeTime ts={state.data.updatedAt} />
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 rounded-card border border-border-subtle p-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <h4 className="text-caption font-medium text-fg">Always sent</h4>
          <ul className="flex flex-col gap-1 text-caption text-fg-muted">
            {ALWAYS.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col gap-2">
          <h4 className="text-caption font-medium text-fg">{mode === 'content' ? 'Also sent' : 'Stays on each machine'}</h4>
          <ul className="flex flex-col gap-1 text-caption text-fg-muted">
            {CONTENT.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
      </div>
      <p className="text-caption text-fg-subtle">Every agent gets the change at once. Secrets are always masked before anything leaves a machine.</p>
    </section>
  );
}

const section: SettingsSection = {
  id: 'data-sent',
  title: 'Data sent',
  description: 'What agents send from each machine: transcripts and diffs, or metadata only.',
  order: 30,
  group: 'security',
  keywords: ['dlp', 'privacy', 'transcript', 'diff', 'metadata', 'content', 'data handling'],
  icon: FileLock2,
  Component: DataSentSettings,
  needs: { module: '/api/policy/data', feature: 'Data choices', plural: true, instead: 'Secrets are always masked before anything leaves a machine.' },
};
export default section;
